import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AccountForm } from '@/components/account/AccountForm';
import { getAccount, updateAccount, getProducts } from '@/services/api';
import { trackAccount } from '@/lib/rum';
import type { Account, AccountUpdateRequest } from '@/types/account';
import type { ApiError } from '@/types/api';
import type { ProductDomain } from '@/types/product';

interface EditAccountModalProps {
  isOpen: boolean;
  accountToken: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditAccountModal: React.FC<EditAccountModalProps> = ({
  isOpen,
  accountToken,
  onClose,
  onSuccess,
}) => {
  const { instance } = useMsal();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountData, setAccountData] = useState<Account | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [productDomains, setProductDomains] = useState<ProductDomain[]>([]);

  useEffect(() => {
    if (isOpen && accountToken) {
      fetchAccountData();
    }
  }, [isOpen, accountToken]);

  useEffect(() => {
    if (isOpen) {
      getProducts(instance)
        .then(response => setProductDomains(response.domains))
        .catch(err => console.error('Failed to fetch products:', err));
    }
  }, [isOpen, instance]);

  const fetchAccountData = async () => {
    setIsLoadingData(true);
    setFetchError(null);
    setAccountData(null);

    try {
      const data = await getAccount(instance, accountToken);
      setAccountData(data);
    } catch (err) {
      console.error('Failed to fetch account:', err);

      const apiError = err as ApiError;

      if (apiError.status === 404) {
        setFetchError('Account not found.');
      } else if (apiError.status === 401) {
        setFetchError('Authentication failed. Please log in again.');
      } else {
        setFetchError('Failed to load account data. Please try again.');
      }
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleSubmit = async (data: AccountUpdateRequest) => {
    setIsSubmitting(true);
    setSubmitError(null);
    trackAccount.updateStarted(accountToken);

    try {
      await updateAccount(instance, accountToken, data as AccountUpdateRequest);
      trackAccount.updateSuccess(accountToken);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to update account:', err);
      trackAccount.updateError(accountToken, err);

      const apiError = err as ApiError;

      if (apiError.status === 404) {
        setSubmitError('Account not found. It may have been deleted.');
      } else if (apiError.status === 400) {
        setSubmitError(apiError.message || 'Invalid data. Please check your inputs and try again.');
      } else if (apiError.status === 401) {
        setSubmitError('Authentication failed. Please log in again.');
      } else {
        setSubmitError('Failed to update account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      setSubmitError(null);
      setFetchError(null);
      onClose();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) {
      setAccountData(null);
      setFetchError(null);
      setSubmitError(null);
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setAccountData(null);
      setFetchError(null);
      setSubmitError(null);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Account</DialogTitle>
          <DialogDescription>
            Modify account settings and configuration.
          </DialogDescription>
        </DialogHeader>

        {/* Loading state */}
        {isLoadingData && (
          <div className="space-y-4 py-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {/* Fetch error */}
        {fetchError && !isLoadingData && (
          <div className="py-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{fetchError}</AlertDescription>
            </Alert>
            <Button variant="outline" className="mt-4" onClick={fetchAccountData}>
              Retry
            </Button>
          </div>
        )}

        {/* Form */}
        {accountData && !isLoadingData && !fetchError && (
          <>
            {submitError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            <AccountForm
              mode="edit"
              initialData={accountData}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={isSubmitting}
              productDomains={productDomains}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
